import { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  ShoppingCart, CheckCircle, Package, Grip, Box, Layers,
  Droplets, Wrench, AlertTriangle, Zap, ArrowRight, Users,
  CalendarDays, FileText, AlertCircle, X,
} from "lucide-react";
import type { Job, Issue } from "@/data/types";
import { useProductRegistry } from "@/data/ProductRegistryContext";
import { useProductOptions, type ProductOption } from "@/data/ProductOptionsContext";
import { cn } from "@/lib/utils";
import { PushProgressTracker } from "@/components/PushProgressTracker";
import { stageKeyToPushStep, type TrackerStageKey } from "@/lib/pushStageNavigation";
import { getJobNextAction } from "@/data/nextActionEngine";
import type { SuggestedAction } from "@/data/suggestedActions";
import { derivePreSpendGate, getGatedCtaLabel } from "@/data/preSpendGate";
import { PreSpendGateBanner } from "@/components/PreSpendGateBanner";
import { getUnmatchedSkus } from "@/lib/skuValidation";
import { UnmatchedSkuBanner } from "@/components/UnmatchedSkuBanner";
import { toast } from "@/hooks/use-toast";

/**
 * Push-Mode next step selected from the inline success panel. The parent
 * uses this to chain into the appropriate follow-up modal without bouncing
 * the user through a toast.
 */
export type OrderingNextStep =
  | { kind: "assign_crew" }
  | { kind: "reschedule" }
  | { kind: "view_permit" }
  | { kind: "resolve_issue" }
  | { kind: "navigate"; to: string }
  | { kind: "done" };

interface OrderingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job;
  /** Open issues for the job — used to suggest "Resolve Issue" as a next step. */
  openIssues?: Issue[];
  onSendToOrdering: (data: { jobId: string; vendor: string; orderType: "Batch" | "Direct" | "Rush"; notes: string }) => void;
  /** Called when user picks a next step from the inline success panel. Parent should close this modal and open the chosen one. */
  onChooseNextStep?: (step: OrderingNextStep) => void;
  /**
   * Reopen directly in the success/next-step picker for the given prior submission.
   * Used by Push-Mode chaining when the user clicks "Back" inside a chained modal —
   * we restore the success panel rather than re-presenting the entry form (the PO
   * was already submitted and we must not allow accidental re-submission).
   */
  reopenSuccess?: { vendor: string; orderType: "Batch" | "Direct" | "Rush"; components: number } | null;
}

/* ── Derive POS material package from job config ── */
interface POSMaterialItem {
  category: string;
  icon: React.ReactNode;
  name: string;
  sku?: string;
  subItems?: string[];
}

function resolveOptionLabel(value: string | undefined, allOptions: ProductOption[]): string {
  if (!value) return "";
  return allOptions.find((o) => o.id === value)?.displayName || value;
}

function derivePOSMaterials(job: Job, allOptions: ProductOption[]): POSMaterialItem[] {
  const cfg = job.projectConfig;
  if (!cfg) return [];
  const items: POSMaterialItem[] = [];

  if (cfg.selectedWallSystem) {
    items.push({ category: "Wall System", icon: <Grip className="h-3.5 w-3.5" />, name: resolveOptionLabel(cfg.selectedWallSystem, allOptions) || cfg.selectedWallSystem });
  }
  if (cfg.selectedBase) {
    items.push({ category: "Base / Pan", icon: <Box className="h-3.5 w-3.5" />, name: resolveOptionLabel(cfg.selectedBase, allOptions) || cfg.selectedBase });
  }
  if (cfg.selectedGlass || cfg.selectedDoor) {
    const parts = [cfg.selectedGlass, cfg.selectedDoor].filter(Boolean) as string[];
    const resolvedParts = parts.map(p => resolveOptionLabel(p, allOptions) || p);
    items.push({ category: "Glass / Door", icon: <Layers className="h-3.5 w-3.5" />, name: resolvedParts.join(" + "), subItems: resolvedParts.length > 1 ? resolvedParts : undefined });
  }
  if (cfg.selectedFixture) {
    items.push({ category: "Fixture Package", icon: <Droplets className="h-3.5 w-3.5" />, name: resolveOptionLabel(cfg.selectedFixture, allOptions) || cfg.selectedFixture });
  }
  if (cfg.selectedGrabBars?.length) {
    const resolvedGrabBars = cfg.selectedGrabBars.map(id => resolveOptionLabel(id, allOptions) || id);
    items.push({ category: "Hardware / Grab Bars", icon: <Wrench className="h-3.5 w-3.5" />, name: resolvedGrabBars.join(", "), subItems: resolvedGrabBars });
  }
  if (cfg.selectedAddOns?.length) {
    const resolvedAddOns = cfg.selectedAddOns.map(id => resolveOptionLabel(id, allOptions) || id);
    items.push({ category: "Add-ons", icon: <Package className="h-3.5 w-3.5" />, name: resolvedAddOns.join(", "), subItems: resolvedAddOns });
  }
  if (cfg.plumbingRelocation) {
    items.push({ category: "Prep / Waterproofing", icon: <Droplets className="h-3.5 w-3.5" />, name: "Plumbing relocation required" });
  }
  return items;
}

/**
 * Map a SuggestedAction returned by the engine to a concrete next-step button
 * the inline success panel can render. Returns null when we have no in-context
 * mapping (caller can fall back to a navigation link).
 */
function suggestedToNextStep(s: SuggestedAction): OrderingNextStep | null {
  if (s.executionMode === "modal") {
    if ((s.icon === "dispatch" || s.icon === "crew")) return { kind: "assign_crew" };
    if (s.icon === "schedule") return { kind: "reschedule" };
    if (s.icon === "issue") return { kind: "resolve_issue" };
  }
  if (s.icon === "permit") return { kind: "view_permit" };
  if (s.link) return { kind: "navigate", to: s.link };
  return null;
}

const NEXT_STEP_META: Record<OrderingNextStep["kind"], { label: string; description: string; icon: React.ElementType }> = {
  assign_crew:   { label: "Assign Crew",     description: "Pick a contractor and lock the install team.", icon: Users },
  reschedule:    { label: "Reschedule",      description: "Confirm or change the install date.",         icon: CalendarDays },
  view_permit:   { label: "View Permit",     description: "Check permit stage and update status.",        icon: FileText },
  resolve_issue: { label: "Resolve Issue",   description: "Close out an open issue on this job.",         icon: AlertCircle },
  navigate:      { label: "Open Next Step",  description: "Continue this workflow on its dedicated page.",icon: ArrowRight },
  done:          { label: "Done",            description: "Close this dialog and return to the queue.",   icon: CheckCircle },
};

export function OrderingModal({ open, onOpenChange, job, openIssues = [], onSendToOrdering, onChooseNextStep, reopenSuccess }: OrderingModalProps) {
  const { allVendors, products } = useProductRegistry();
  const { options: allOptions } = useProductOptions();
  const [vendor, setVendor] = useState("");
  const [orderType, setOrderType] = useState<"Batch" | "Direct" | "Rush">("Batch");
  const [notes, setNotes] = useState("");
  const [sent, setSent] = useState(false);
  const [sentSummary, setSentSummary] = useState<{ vendor: string; orderType: string; components: number } | null>(null);

  // Reset internal state whenever the modal opens for a new job.
  // If `reopenSuccess` is provided, restore the success/next-step picker state
  // (Push-Mode "Back" from a chained modal) — never re-present the entry form.
  useEffect(() => {
    if (open) {
      if (reopenSuccess) {
        setSent(true);
        setSentSummary(reopenSuccess);
        setVendor(reopenSuccess.vendor);
        setOrderType(reopenSuccess.orderType);
        setNotes("");
      } else {
        setSent(false);
        setSentSummary(null);
        setVendor("");
        setOrderType("Batch");
        setNotes("");
      }
    }
  }, [open, job.id, reopenSuccess]);

  const materials = useMemo(() => derivePOSMaterials(job, allOptions), [job, allOptions]);

  const productLabel = (() => {
    const mat = job.projectConfig?.bathroomMaterials?.[0] ?? job.projectConfig?.primaryMaterial ?? "";
    const pt = job.projectType === "Tub to Shower Conversion" ? "Shower Conversion" : job.projectType;
    return [mat, pt].filter(Boolean).join(" ");
  })();

  // Auto-suggest vendor from material
  const suggestedVendor = useMemo(() => {
    const names = materials.map((m) => m.name.toLowerCase());
    const matchedVendors = new Set(
      products
        .filter((p) => names.some((n) => n.includes(p.productName.toLowerCase())))
        .map((p) => String(p.vendor || "").trim())
        .filter((vendor) => vendor && vendor.toLowerCase() !== "unassigned"),
    );
    if (matchedVendors.size > 1) return "Mixed";
    const [singleVendor] = [...matchedVendors];
    return singleVendor || allVendors.find((v) => String(v || "").trim().toLowerCase() !== "unassigned") || "";
  }, [materials, products, allVendors]);

  const vendorList = useMemo(() => {
    const fromProducts = products.map((p) => p.vendor).filter(Boolean);
    const set = new Set<string>(["Mixed", ...allVendors, ...fromProducts]);
    return Array.from(set)
      .map((v) => String(v || "").trim())
      .filter((v) => v.length > 0 && v.toLowerCase() !== "unassigned")
      .sort((a, b) => {
        if (a === "Mixed") return -1;
        if (b === "Mixed") return 1;
        return a.localeCompare(b);
      });
  }, [allVendors, products]);

  useEffect(() => {
    if (!open) return;
    if (!vendor && suggestedVendor) setVendor(suggestedVendor);
  }, [open, suggestedVendor, vendor]);

  // Compute the next plausible step assuming materials are now ordered.
  const projectedNextSteps = useMemo<OrderingNextStep[]>(() => {
    const projected: Job = { ...job, materialReadiness: "Ordered" as Job["materialReadiness"] };
    const next = getJobNextAction(projected, openIssues);
    const seen = new Set<string>();
    const out: OrderingNextStep[] = [];
    if (next) {
      const mapped = suggestedToNextStep(next);
      if (mapped) {
        const key = mapped.kind === "navigate" ? `nav:${mapped.to}` : mapped.kind;
        if (!seen.has(key)) { seen.add(key); out.push(mapped); }
      }
    }
    // Always offer Assign Crew + Reschedule as universal Push Mode follow-ups
    // when not already covered by the engine.
    if (!seen.has("assign_crew")) { out.push({ kind: "assign_crew" }); seen.add("assign_crew"); }
    if (!seen.has("reschedule"))  { out.push({ kind: "reschedule"  }); seen.add("reschedule");  }
    return out.slice(0, 3);
  }, [job, openIssues]);

  const handleSubmit = () => {
    const selectedVendor = (vendor || suggestedVendor || "").trim();
    if (!selectedVendor) {
      toast({ title: "Vendor required", description: "Select a vendor before sending to ordering.", variant: "destructive" });
      return;
    }
    onSendToOrdering({
      jobId: job.id,
      vendor: selectedVendor,
      orderType,
      notes,
    });
    setSentSummary({
      vendor: selectedVendor,
      orderType,
      components: materials.length,
    });
    setSent(true);
  };

  const handlePickNext = (step: OrderingNextStep) => {
    if (onChooseNextStep) onChooseNextStep(step);
    else onOpenChange(false);
  };

  // Pre-spend gate: ordering = direct company spend → must clear all 3 gates.
  const gate = useMemo(() => derivePreSpendGate(job), [job]);
  const effectiveVendor = (vendor || suggestedVendor || "").trim();
  const unmatchedSkus = useMemo(
    () => products.length > 0 ? getUnmatchedSkus(job.materialsList, products) : [],
    [job.materialsList, products],
  );
  const canSubmit = materials.length > 0 && gate.cleared && effectiveVendor.length > 0 && unmatchedSkus.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-border">
          <DialogTitle className="text-[16px] font-bold flex items-center gap-2">
            {sent ? (
              <CheckCircle className="h-4.5 w-4.5 text-success" />
            ) : (
              <ShoppingCart className="h-4.5 w-4.5 text-primary" />
            )}
            {sent ? `Sent to Ordering — ${job.customerName}` : `Send to Ordering — ${job.customerName}`}
          </DialogTitle>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {productLabel} • {job.address.city}, {job.address.state}
          </p>
        </DialogHeader>

        {/* Push Progress — visible during execution */}
        <div className="px-6 pt-3 pb-1">
          <PushProgressTracker
            job={sent ? { ...job, materialReadiness: "Ordered" as Job["materialReadiness"] } : job}
            activeKey={sent ? undefined : "materials"}
            onStageClick={(key) => {
              if (key === "materials") return;
              const step = stageKeyToPushStep(key as TrackerStageKey, job.id);
              if (step.kind === "generate_po") return;
              onChooseNextStep?.(step as OrderingNextStep);
            }}
          />
        </div>

        {sent ? (
          // ─── INLINE SUCCESS STATE — user picks the next step in-place ───
          <div className="p-6 space-y-5 overflow-y-auto" style={{ maxHeight: "65vh" }}>
            {/* Success summary */}
            <div className="rounded-lg border border-success/30 bg-success/5 p-4 flex items-start gap-3">
              <div className="h-8 w-8 rounded-full bg-success/15 flex items-center justify-center shrink-0">
                <CheckCircle className="h-4.5 w-4.5 text-success" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-bold text-foreground">PO sent successfully</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {sentSummary?.components} components · {sentSummary?.vendor} · {sentSummary?.orderType}
                </p>
              </div>
            </div>

            {/* Next step picker */}
            <div className="space-y-2">
              <h3 className="text-[12px] font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                <Zap className="h-3.5 w-3.5 text-primary" />
                Continue pushing this job
              </h3>
              <p className="text-[11px] text-muted-foreground">
                Pick the next step — it'll open in a focused dialog without leaving this context.
              </p>

              <div className="grid gap-2 mt-3">
                {projectedNextSteps.map((step) => {
                  const meta = NEXT_STEP_META[step.kind];
                  const Icon = meta.icon;
                  return (
                    <button
                      key={step.kind === "navigate" ? `nav:${step.to}` : step.kind}
                      onClick={() => handlePickNext(step)}
                      className="group flex items-center gap-3 rounded-lg border border-border bg-card hover:border-primary/40 hover:bg-primary/5 px-4 py-3 text-left transition-colors"
                    >
                      <div className="h-8 w-8 rounded-md bg-muted/40 group-hover:bg-primary/10 flex items-center justify-center shrink-0 text-muted-foreground group-hover:text-primary transition-colors">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-semibold text-foreground">{meta.label}</p>
                        <p className="text-[10px] text-muted-foreground">{meta.description}</p>
                      </div>
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                    </button>
                  );
                })}

                {/* Always-available "Done" exit */}
                <button
                  onClick={() => handlePickNext({ kind: "done" })}
                  className="group flex items-center gap-3 rounded-lg border border-dashed border-border hover:border-muted-foreground/40 px-4 py-2.5 text-left transition-colors"
                >
                  <div className="h-7 w-7 rounded-md flex items-center justify-center shrink-0 text-muted-foreground">
                    <X className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-semibold text-foreground">Done for now</p>
                    <p className="text-[10px] text-muted-foreground">Close and return to the queue.</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        ) : (
          // ─── ENTRY STATE — vendor selection + confirm ───
          <div className="flex flex-1 overflow-hidden" style={{ height: "calc(90vh - 120px)", maxHeight: "65vh" }}>
            {/* LEFT — Read-only material summary */}
            <ScrollArea className="w-[60%] border-r border-border">
              <div className="p-6 space-y-4">
                <h3 className="text-[12px] font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                  <Package className="h-3.5 w-3.5 text-muted-foreground" />
                  POS Material Package
                  <span className="text-[10px] font-medium text-muted-foreground normal-case tracking-normal">
                    ({materials.length} components) · Read-only
                  </span>
                </h3>

                {materials.length > 0 ? (
                  <div className="space-y-2">
                    {materials.map(item => (
                      <div key={item.category} className="rounded-lg border border-border bg-card p-3">
                        <div className="flex items-start gap-2.5">
                          <div className="h-7 w-7 rounded-md bg-muted/50 flex items-center justify-center shrink-0 text-muted-foreground mt-0.5">
                            {item.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">{item.category}</p>
                            <p className="text-[12px] font-semibold text-foreground mt-0.5">{item.name}</p>
                            {item.subItems && item.subItems.length > 1 && (
                              <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {item.subItems.map((si, i) => (
                                  <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-muted/50 text-muted-foreground font-medium">
                                    {si}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <CheckCircle className="h-3.5 w-3.5 text-success shrink-0 mt-1" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-warning/5 border border-warning/20">
                    <AlertTriangle className="h-4 w-4 text-warning shrink-0" />
                    <span className="text-[12px] text-warning font-medium">No materials defined in POS configuration</span>
                  </div>
                )}

                {/* Source badge */}
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/20 border border-border/40">
                  <span className="text-[10px] font-semibold text-muted-foreground">Source: POS Work Order</span>
                  <span className="text-[10px] text-muted-foreground/50">•</span>
                  <span className="text-[10px] font-semibold text-success">Build Review Approved ✓</span>
                </div>
              </div>
            </ScrollArea>

            {/* RIGHT — Vendor + Order Type + Actions */}
            <ScrollArea className="w-[40%]">
              <div className="p-6 space-y-5">
                {/* Vendor Selection */}
                <div className="space-y-2">
                  <label className="text-[12px] font-bold text-foreground uppercase tracking-wider">Vendor</label>
                  <Select value={vendor || suggestedVendor} onValueChange={setVendor}>
                    <SelectTrigger className="text-[12px]">
                      <SelectValue placeholder="Select vendor..." />
                    </SelectTrigger>
                    <SelectContent>
                      {vendorList.map(v => (
                        <SelectItem key={v} value={v} className="text-[12px]">
                          {v} {v === suggestedVendor ? "(Recommended)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {suggestedVendor && (
                    <p className="text-[10px] text-muted-foreground">
                      Recommended: <span className="font-semibold text-success">{suggestedVendor}</span> based on material mapping
                    </p>
                  )}
                  {suggestedVendor === "Mixed" && (
                    <p className="text-[10px] text-warning font-medium">
                      Mixed vendor BOM detected. POs will be generated per vendor group.
                    </p>
                  )}
                </div>

                {/* Order Type */}
                <div className="space-y-2">
                  <label className="text-[12px] font-bold text-foreground uppercase tracking-wider">Order Type</label>
                  <div className="grid grid-cols-3 gap-2">
                    {(["Batch", "Direct", "Rush"] as const).map(type => (
                      <button
                        key={type}
                        onClick={() => setOrderType(type)}
                        className={cn(
                          "px-3 py-2.5 rounded-lg border text-[11px] font-semibold transition-all text-center",
                          orderType === type
                            ? type === "Rush"
                              ? "border-destructive/40 bg-destructive/10 text-destructive"
                              : "border-primary/40 bg-primary/10 text-primary"
                            : "border-border hover:border-muted-foreground/30"
                        )}
                      >
                        {type === "Rush" && <Zap className="h-3 w-3 inline mr-1" />}
                        {type}
                      </button>
                    ))}
                  </div>
                  {orderType === "Rush" && (
                    <p className="text-[10px] text-destructive font-medium">
                      ⚠ Rush orders may incur higher costs and bypass batch pricing
                    </p>
                  )}
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <label className="text-[12px] font-bold text-foreground uppercase tracking-wider">Notes</label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional ordering notes..."
                    className="text-[12px] min-h-[60px] bg-card"
                  />
                </div>

                {/* Summary */}
                <div className="rounded-lg border border-border/40 bg-muted/10 p-3 space-y-1.5">
                  <p className="text-[10px] font-bold text-foreground uppercase tracking-wider">Order Summary</p>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-muted-foreground">Components</span>
                    <span className="font-semibold">{materials.length}</span>
                  </div>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-muted-foreground">Vendor</span>
                    <span className="font-semibold">{effectiveVendor || "—"}</span>
                  </div>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-muted-foreground">Type</span>
                    <span className={cn("font-semibold", orderType === "Rush" ? "text-destructive" : "text-foreground")}>{orderType}</span>
                  </div>
                </div>

                {/* Pre-spend gate — block ordering until review + rescission + financial all clear. */}
                {!gate.cleared && (
                  <PreSpendGateBanner
                    gate={gate}
                    context="Materials cannot be ordered yet — company spend is gated."
                  />
                )}

                {/* Unmatched SKU gate — block ordering until all SKUs exist in the product registry. */}
                <UnmatchedSkuBanner skus={unmatchedSkus} />

                {/* Actions */}
                <div className="space-y-2 pt-2 border-t border-border">
                  <Button
                    className="w-full h-10 text-[12px] font-bold gap-1.5"
                    disabled={!canSubmit}
                    onClick={handleSubmit}
                    title={!gate.cleared ? gate.shortReason : undefined}
                  >
                    <ShoppingCart className="h-3.5 w-3.5" />
                    {gate.cleared
                      ? (materials.length > 0 ? "Send to Ordering (ready)" : "Send to Ordering")
                      : getGatedCtaLabel("Send to Ordering", gate)}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full h-9 text-[11px]"
                    onClick={() => onOpenChange(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </ScrollArea>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
